<template>
  <div v-if="ready">
    <empty-placeholder v-if="emptyProgramme && emptyCompetencies"
      title="No modules available in this programme"
      :info="emptyProgrammeText"
      :fa-icon="emptyIcon"
    ></empty-placeholder>
    <template v-else>
      <div class="filters-container">
        <div class="nav-tabs-wrapper">
          <k-nav-tabs :currentTab="currentTab" :tabs="showTabs"></k-nav-tabs>
        </div>
        <tab-controls :currentTab="currentTab"
          :sortDescending="sortDescending"
          :sortMode="competencySortMode"
          :setModulesLayout="setModulesLayout"
          @toggleSort="toggleSort"
          @toggleLayout="toggleLayout"
          @filter="filterCompetencies"
          @changeSortMode="changeCompetencySortMode"
        />
      </div>
      <section v-if="activeTabLabel" aria-labelledby="programme-tab-heading">
        <h2 id="programme-tab-heading" class="sr-only">{{ activeTabLabel }}</h2>
        <router-view
          v-bind="pageProps"
          :key="programmeId"
        ></router-view>
      </section>
    </template>
  </div>
</template>

<style>
.grid-view {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(49%, 1fr));
  grid-column-gap: 15px;
}

.grid-view .panel-horizontal .module-content {
  gap: 15px;
  justify-content: flex-start;
}

.grid-view .module-body {
  flex-direction: column;
  text-align: left;
}

.grid-view .module-progress-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  text-align: left;
  column-gap: 15px;
  justify-content: space-between;
}

.grid-view .completion-date,
.grid-view .module-body,
.grid-view .module-progress-container {
  margin: 0;
  padding: 0;
}

.grid-view .k-progress {
  flex: 1 1 25%;
  min-width: 150px;
}

.grid-view .completion-date {
  flex: 1 1 70%;
  max-width: 340px;
}

@media (max-width: 842px) {
  .grid-view .module-button-container {
    text-align: center;
  }

  .grid-view .module-description {
    max-height: unset;
  }
}

@media (min-width: 544px) and (max-width: 670px) {
  .grid-view .module-progress-container {
    display: block;
  }

  .grid-view .k-progress {
    width: 100%;
    max-width: unset;
  }
}

@media (max-width: 768px) {
  .grid-view .k-progress {
    max-width: unset;
    width: 100%;
  }
}
</style>

<style scoped>
.filters-container {
  margin-bottom: 15px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.filters-container ul {
  float: left;
}

.nav-tabs-wrapper {
  margin: inherit;
}

.filters-container .btn-default:focus,
.filters-container .btn-default:hover {
  background-color: var(--kate-background-alt-alpha);
  border-color: var(--kate-background-alt-alpha);
}

</style>

<script>
import useGlobalStore from '@stores/global';
import useProgrammeCompetencyStore from '@stores/programme-competency-store';
import TimeMixin from '@mixins/time-mixins';
import StorageMixin from '@mixins/storage-mixins';
import KNavTabs from '@base-components/k-nav-tabs.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import { genericSort } from '@utils/sort-by-object-property';
import { COMPETENCY_SORT_MODES, sortCompetencies, filterCompetencyList } from '@utils/competency-list';
import { storeToRefs } from 'pinia';
import TabControls from './components/programme-content-tab-controls.vue';

export default {
  props: {
    programmeId: {
      type: Number,
      required: true,
    },
  },

  components: {
    EmptyPlaceholder,
    KNavTabs,
    TabControls,
  },

  mixins: [TimeMixin, StorageMixin],

  data() {
    const competencyStore = useProgrammeCompetencyStore();
    const { competencies } = storeToRefs(competencyStore);

    return {
      competencyStore,
      programmeModulesReady: false,
      programmeModules: [],
      filterText: '',
      selectedCategory: undefined,
      store: useGlobalStore(),
      competenciesReady: false,
      competencies,
      categoryFilterFunc: () => true,
      competencySortMode: COMPETENCY_SORT_MODES.DEFAULT,
      projectsReady: false,
      projects: [],
    };
  },

  beforeMount() {
    this.getCompetencies();
    this.getProgrammeModules();
    if (this.$featureFlags.isEnabled('kate-projects-app')) {
      this.getProjects();
    } else {
      this.projectsReady = true;
    }

    const sortingState = this.loadSetting('programme-content', 'sortingState');
    if (sortingState !== undefined) {
      this.store.setModulesSorting(sortingState);
    }

    const competencySortMode = this.loadSetting('programme-content', 'competencySortMode');
    if (competencySortMode !== undefined) {
      this.competencySortMode = competencySortMode;
    }

    // Layout
    const layoutState = this.loadSetting('programme-content', 'layoutState');
    if (layoutState !== undefined) {
      this.store.setModulesLayout(layoutState);
    }
  },

  watch: {
    ready(newVal) {
      if (newVal) {
        this.$emit('programmeContentReady');
      }
    },
    programmeId() {
      this.getProgrammeModules();
      this.getCompetencies();
      if (this.$featureFlags.isEnabled('kate-projects-app')) {
        this.getProjects();
      } else {
        this.projectsReady = true;
      }
    },
  },

  computed: {
    ready() {
      return this.programmeModulesReady && this.competenciesReady && this.projectsReady;
    },

    emptyIcon() {
      return 'fa-duotone fa-hourglass-start';
    },

    tabs() {
      return [
        {
          name: 'Curriculum',
          routeName: 'programme_curriculum',
          routeParams: {
            programmeId: this.programmeId,
          },
        },
        {
          name: 'Milestones',
          routeName: 'programme_milestones',
          routeParams: {
            programmeId: this.programmeId,
          },
        },
        {
          name: 'Competencies',
          routeName: 'programme_competencies',
          routeParams: {
            programmeId: this.programmeId,
          },
        },
        {
          name: 'Projects',
          routeName: 'programme_projects',
          routeParams: {
            programmeId: this.programmeId,
          },
        },
      ];
    },

    currentTab() {
      return this.$route.name;
    },

    showTabs() {
      return this.tabs.filter(tab => {
        if (tab.name === 'Competencies') {
          return this.$featureFlags.isEnabled('kate-competencies-feature') && !this.emptyCompetencies;
        }
        if (tab.name === 'Projects') {
          return this.$featureFlags.isEnabled('kate-projects-app');
        }
        return true;
      });
    },

    activeTabLabel() {
      return this.showTabs.find(tab => tab.routeName === this.currentTab)?.name;
    },

    pageProps() {
      if (this.currentTab === 'programme_curriculum') {
        return {
          programmeId: this.programmeId,
          orderedModules: this.orderedModules,
          setModulesLayout: this.setModulesLayout,
        };
      } if (this.currentTab === 'programme_milestones') {
        return {
          programmeId: this.programmeId,
          orderedModules: this.orderedModules,
          setModulesLayout: this.setModulesLayout,
        };
      } if (this.currentTab === 'programme_competencies') {
        return {
          programmeId: this.programmeId,
          competencies: this.filteredCompetencies,
          setModulesLayout: this.setModulesLayout,
        };
      } if (this.currentTab === 'programme_projects') {
        return {
          programmeId: this.programmeId,
          competencies: this.filteredCompetencies,
          projects: this.projects,
          projectsReady: this.projectsReady,
          getProjects: this.getProjects,
        };
      }
      return undefined;
    },

    programmeScheduleLink() {
      return `<a href="/programmes/${this.programmeId}/schedule">Schedule Page</a>`;
    },

    emptyProgramme() {
      return Boolean(
        this.programmeModulesReady
        && !(this.programmeModules ?? []).some(m => m.released),
      );
    },

    emptyCompetencies() {
      return Boolean(this.competencies.length === 0 && this.competenciesReady);
    },

    orderedModules() {
      if (!this.programmeModules) return [];

      // Filter based on current tab
      let filteredModules = this.programmeModules.filter(m => {
        if (this.currentTab === 'programme_curriculum') {
          return m.track === 'curriculum';
        }
        if (this.currentTab === 'programme_milestones') {
          return m.track === 'milestone';
        }
        return false; // Default case
      });

      // Hide not released modules
      filteredModules = filteredModules.filter(m => m.released);

      // Sort after filtering
      filteredModules = filteredModules.slice().sort(this.sortModule);

      // Apply descending order if needed
      return this.sortDescending ? filteredModules.reverse() : filteredModules;
    },

    sortDescending() {
      return this.store.sortModulesDescending;
    },

    setModulesLayout() {
      return this.store.isGridLayout;
    },

    emptyProgrammeText() {
      return `You can check the ${this.programmeScheduleLink} to see when each module will be released in this programme.
      <br>If you think there is an issue, please contact Cambridge Spark`;
    },

    sortedCompetencies() {
      return sortCompetencies(this.competencies, this.competencySortMode);
    },

    filteredCompetencies() {
      return filterCompetencyList(this.sortedCompetencies, {
        text: this.filterText,
        categoryFilter: this.categoryFilterFunc,
        parseTimestamp: this.parseTimestamp,
      });
    },
  },

  methods: {
    getProgrammeModules() {
      if (!this.programmeId) {
        return;
      }
      this.programmeModulesReady = false;
      this.$logger.info('Getting modules for programme', {
        programmeId: this.programmeId,
      }, true);
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/modules`)
        .then(response => {
          this.programmeModules = response.data.modules;
          this.programmeModulesReady = true;
          this.$logger.info('Got modules for programme', {
            programmeId: this.programmeId,
          });
        })
        .catch(err => {
          this.programmeModules = [];
          if (this.$http.errIn(err, [404])) {
            this.$logger.warn('No modules found for programme', {
              programmeId: this.programmeId,
            });
            this.$ktoast.info('No modules were found for this programme', { position: 'top-center' });
          } else if (this.$http.isWarning(err)) {
            this.$logger.warn('Error while getting modules for programme', {
              programmeId: this.programmeId,
            }, err);
            this.$ktoast.showError(err, true);
          } else {
            this.$logger.error('Error while getting modules for programme', {
              programmeId: this.programmeId,
            }, err);
            this.$ktoast.showError(err, true);
          }
        })
        .then(() => {
          this.programmeModulesReady = true;
        });
    },

    getCompetencies() {
      this.competenciesReady = false;
      return this.competencyStore.fetchCompetencies(this.programmeId)
        .catch(error => {
          this.$logger.error('Error fetching competencies', undefined, error);
        }).then(() => {
          this.competenciesReady = true;
        });
    },

    getProjects() {
      this.projectsReady = false;
      this.$logger.info('Fetching Projects');
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/projects`)
        .then(response => {
          this.$logger.info('Successfully fetched projects');
          this.projects = response.data || [];
        })
        .catch(err => {
          this.$logger.autowarn('Error fetching projects', {}, err);
          this.$ktoast.warning('Error fetching projects');
          this.projects = [];
        }).then(() => {
          this.projectsReady = true;
        });
    },

    sortModule(a, b) {
      if (a.number && b.number) {
        return genericSort(a.number, b.number);
      }
      if (!a.number && !b.number) {
        return genericSort(a.name, b.name);
      }
      if (a.number && !b.number) {
        return -1;
      }
      return 1;
    },

    toggleSort() {
      this.store.toggleModulesSortingOrder();
      this.saveSetting('programme-content', 'sortingState', this.sortDescending);
    },

    changeCompetencySortMode(mode) {
      this.competencySortMode = mode;
      this.saveSetting('programme-content', 'competencySortMode', this.competencySortMode);
    },

    toggleLayout() {
      this.store.toggleModulesLayout();
      this.saveSetting('programme-content', 'layoutState', this.setModulesLayout);
    },

    filterCompetencies({ text, categoryFilter }) {
      this.filterText = text;
      this.categoryFilterFunc = categoryFilter || (() => true);
    },
  },
};
</script>
